import React from 'react';

import { FormControl, Checkbox, FormErrorMessage, Stack } from '@chakra-ui/react';
import { cn } from '@easydatasolution/ui-react/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { Link } from 'gatsby';
import { useForm, useFieldArray, type SubmitHandler } from 'react-hook-form';

import {
	DigitalizationAttendeesFormData,
	digitalizationAttendeesFormSchema,
	type AttendeesFormData,
} from './schema';
import { MAX_ATTENDEES_PER_COURSE } from '../../misc/constants';
import { Url } from '../../misc/enums';
import { Attendee, createAttendee } from '../../models/Attendee';
import { Digitalization } from '../../types/digitalization';
import { Button } from '../Button/Button';
import { FormInput } from '../FormInput/FormInput';
import { FormSelect } from '../FormSelect/FormSelect';

interface DigitalizationAttendeesFormProps extends React.ComponentPropsWithoutRef<'form'> {
	event: Digitalization['events'][number];
	handleSubmit: (attendees: Attendee[]) => void;
	isLoading: boolean;
}

export function DigitalizationAttendeesForm({
	event,
	handleSubmit,
	isLoading,
	className,
	...rest
}: DigitalizationAttendeesFormProps) {
	const {
		control,
		register,
		handleSubmit: handleFormSubmit,
		formState: { errors },
	} = useForm<DigitalizationAttendeesFormData>({
		resolver: zodResolver(digitalizationAttendeesFormSchema),
		defaultValues: {
			attendees: [{ anrede: '' as 'Herr', vorname: '', nachname: '', email: '' }],
			agbAccepted: false,
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'attendees',
	});

	const addAttendee = () => {
		if (fields.length < MAX_ATTENDEES_PER_COURSE) {
			const newAttendeeCount = fields.length + 1;
			append({ anrede: '' as 'Herr', vorname: '', nachname: '', email: '' });
			window?.plausible?.('event_attendee_added', {
				props: {
					event_uuid: event.uuid,
					event_title: event.title,
					new_attendee_count: String(newAttendeeCount),
					total_price: String((event.price ?? 0) * newAttendeeCount),
				},
			});
		}
	};

	const removeAttendee = (index: number) => {
		if (fields.length > 1) {
			const newAttendeeCount = fields.length - 1;
			remove(index);
			window?.plausible?.('event_attendee_removed', {
				props: {
					event_uuid: event.uuid,
					event_title: event.title,
					new_attendee_count: String(newAttendeeCount),
					total_price: String((event.price ?? 0) * newAttendeeCount),
				},
			});
		}
	};

	const onSubmit: SubmitHandler<AttendeesFormData> = (data) => {
		if (!data) return;

		const attendees = data.attendees.map((attendee) => {
			const { anrede, vorname, nachname, email } = attendee;
			return createAttendee({ anrede, vorname, nachname, email });
		});

		handleSubmit(attendees);
	};

	return (
		<form
			onSubmit={handleFormSubmit(onSubmit)}
			className={cn(`flex flex-col gap-8`, className)}
			{...rest}
		>
			{fields.map((field, index) => (
				<div key={field.id} className="bg-slate-100">
					<div className="mt-3 flex items-center justify-between">
						<div className="bg-secondary-500 px-5 py-2.5 font-semibold text-white">
							Person {index + 1}
						</div>
						<Button
							className="mr-5 bg-white px-3"
							onClick={() => removeAttendee(index)}
							variant="ghost"
							colorScheme="red"
							disabled={fields.length === 1}
							size="sm"
							leftIcon={<FaTrash className="h-3 w-3" />}
						>
							Entfernen
						</Button>
					</div>
					<div className="flex flex-col gap-3 p-5">
						<FormSelect
							label="Anrede"
							{...register(`attendees.${index}.anrede`)}
							error={errors.attendees?.[index]?.anrede}
							required
						>
							<option value="">auswählen</option>
							<option value="Herr">Herr</option>
							<option value="Frau">Frau</option>
							<option value="Divers">Divers</option>
						</FormSelect>
						<FormInput
							label="Vorname"
							{...register(`attendees.${index}.vorname`)}
							error={errors.attendees?.[index]?.vorname}
							required
						/>
						<FormInput
							label="Nachname"
							{...register(`attendees.${index}.nachname`)}
							error={errors.attendees?.[index]?.nachname}
							required
						/>
						<FormInput
							label="Firmen-E-Mail"
							type="email"
							{...register(`attendees.${index}.email`)}
							error={errors.attendees?.[index]?.email}
							required
						/>
					</div>
				</div>
			))}

			<FormControl isInvalid={!!errors.agbAccepted}>
				<Checkbox {...register('agbAccepted', { required: true })} className="text-secondary-700">
					Ich akzeptiere die{' '}
					<Link to={`/${Url.AGB}/`} className="underline">
						AGBs
					</Link>
					.
				</Checkbox>
				<FormErrorMessage>Bitte akzeptieren Sie die AGBs.</FormErrorMessage>
			</FormControl>
			<Stack spacing={3}>
				<Button
					variant="outline"
					onClick={addAttendee}
					disabled={fields.length >= MAX_ATTENDEES_PER_COURSE || isLoading}
				>
					Person hinzufügen
				</Button>

				<Button type="submit" disabled={fields.length === 0 || isLoading} isLoading={isLoading}>
					Teilnehmen
				</Button>
			</Stack>
		</form>
	);
}
